
import React from 'react';
import { TopicType } from '@/types/assistant';
import { useAiAssistant } from '@/contexts/AiAssistantContext';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Crown } from 'lucide-react';

interface SidebarUsageDisplayProps {
  topic: TopicType;
  className?: string;
}

export const SidebarUsageDisplay: React.FC<SidebarUsageDisplayProps> = ({
  topic,
  className = ''
}) => {
  const { getUsageForTopic } = useAiAssistant();
  const usage = getUsageForTopic?.(topic);

  if (!usage) {
    return null;
  }

  const dailyPercentage = (usage.daily_usage / usage.daily_limit) * 100;
  const monthlyPercentage = (usage.monthly_usage / usage.monthly_limit) * 100;
  
  const isDailyExceeded = usage.daily_usage >= usage.daily_limit;
  const isMonthlyExceeded = usage.monthly_usage >= usage.monthly_limit;
  const isLimitExceeded = isDailyExceeded || isMonthlyExceeded;
  const isNearLimit = dailyPercentage >= 80 || monthlyPercentage >= 80;

  const getProgressColor = (percentage: number, isExceeded: boolean) => {
    if (isExceeded) return "bg-destructive";
    if (percentage >= 80) return "bg-warning";
    return "bg-primary";
  };

  return (
    <div className={`p-3 border-t border-border/50 bg-gradient-to-b from-muted/10 to-background/50 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-medium text-foreground">Usage</span>
        <Badge 
          variant={isLimitExceeded ? "destructive" : "secondary"} 
          className="text-xs h-5 px-2"
        >
          {usage.package_name}
        </Badge>
      </div>

      {/* Daily Usage - Inline */}
      <div className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Daily</span>
            <span className={`text-xs font-medium ${isDailyExceeded ? 'text-destructive' : 'text-foreground'}`}>
              {usage.daily_usage}/{usage.daily_limit}
            </span>
          </div>
          <Progress 
            value={Math.min(dailyPercentage, 100)} 
            className="h-1.5"
            indicatorClassName={getProgressColor(dailyPercentage, isDailyExceeded)}
          />
        </div>

        {/* Monthly Usage - Inline */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Monthly</span>
            <span className={`text-xs font-medium ${isMonthlyExceeded ? 'text-destructive' : 'text-foreground'}`}>
              {usage.monthly_usage}/{usage.monthly_limit}
            </span>
          </div>
          <Progress 
            value={Math.min(monthlyPercentage, 100)} 
            className="h-1.5"
            indicatorClassName={getProgressColor(monthlyPercentage, isMonthlyExceeded)}
          />
        </div>
      </div>

      {/* Status Messages */}
      {isLimitExceeded && (
        <div className="flex items-center gap-1.5 mt-3 p-2 bg-destructive/10 border border-destructive/20 rounded-md text-xs text-destructive">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          <span className="flex-1">Limit reached</span>
          <Crown className="h-3 w-3 text-warning flex-shrink-0" />
        </div>
      )}

      {!isLimitExceeded && isNearLimit && (
        <div className="flex items-center gap-1.5 mt-3 p-2 bg-warning/10 border border-warning/20 rounded-md text-xs text-warning-foreground">
          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
          <span>Near limit</span>
        </div>
      )}
    </div>
  );
};
